import { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import PageHeader from '../../components/PageHeader';
import styles from './SupplierReports.module.css';

function formatWeekLabel(mondayStr) {
  if (!mondayStr) return '—';
  const start = new Date(mondayStr + 'T00:00:00');
  const end   = new Date(mondayStr + 'T00:00:00');
  end.setDate(end.getDate() + 6);
  const fmt = d => d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}, ${end.getFullYear()}`;
}

function php(n) {
  return `₱${(n || 0).toLocaleString()}`;
}

export default function SupplierReports() {
  const [records,    setRecords]    = useState([]);
  const [weekFilter, setWeekFilter] = useState('All');

  useEffect(() => {
    const q = query(collection(db, 'weeklyProfits'), orderBy('weekStart', 'desc'));
    return onSnapshot(q, snap => setRecords(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, []);

  const uniqueWeeks = useMemo(() => {
    const seen = new Set();
    const weeks = [];
    for (const r of records) {
      if (!seen.has(r.weekStart)) {
        seen.add(r.weekStart);
        weeks.push({ weekStart: r.weekStart, label: r.weekLabel || formatWeekLabel(r.weekStart) });
      }
    }
    return weeks;
  }, [records]);

  const filtered = weekFilter === 'All'
    ? records
    : records.filter(r => r.weekStart === weekFilter);

  // Aggregate totals across all filtered records
  const totals = useMemo(() => filtered.reduce(
    (acc, r) => ({
      revenue: acc.revenue + (r.weeklyRevenue || 0),
      expense: acc.expense + (r.supplyExpense  || 0),
      net:     acc.net     + (r.netProfit      || 0),
    }),
    { revenue: 0, expense: 0, net: 0 },
  ), [filtered]);

  // Weekly summary (aggregated per week across all branches)
  const weeklySummary = useMemo(() => {
    const map = {};
    for (const r of records) {
      const key = r.weekStart;
      if (!map[key]) map[key] = { weekStart: key, weekLabel: r.weekLabel || formatWeekLabel(key), revenue: 0, expense: 0, net: 0, branches: 0 };
      map[key].revenue  += r.weeklyRevenue || 0;
      map[key].expense  += r.supplyExpense  || 0;
      map[key].net      += r.netProfit      || 0;
      map[key].branches += 1;
    }
    return Object.values(map).sort((a, b) => b.weekStart.localeCompare(a.weekStart));
  }, [records]);

  return (
    <div className={styles.page}>
      <PageHeader title="Branch Weekly Profits" description="Collective weekly profit reports from all branches" />

      {/* ── Summary stats ── */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{records.length}</span>
          <span className={styles.statLabel}>Total Entries</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{php(totals.expense)}</span>
          <span className={styles.statLabel}>Weekly Order Expense</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{php(totals.revenue)}</span>
          <span className={styles.statLabel}>Weekly Revenue</span>
        </div>
        <div className={styles.statCard}>
          <span className={`${styles.statValue} ${totals.net >= 0 ? styles.positive : styles.negative}`}>
            {php(totals.net)}
          </span>
          <span className={styles.statLabel}>Weekly Net Profit</span>
        </div>
      </div>

      {/* ── Weekly Summary table ── */}
      <div className={styles.sectionTitle}>Weekly Summary (All Branches)</div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Week</th>
              <th>Branches Reported</th>
              <th>Weekly Order Expense</th>
              <th>Weekly Revenue</th>
              <th>Weekly Net Profit</th>
            </tr>
          </thead>
          <tbody>
            {weeklySummary.length === 0 ? (
              <tr><td colSpan={5} className={styles.empty}>No weekly profit records yet.</td></tr>
            ) : weeklySummary.map((w, i) => (
              <tr key={w.weekStart} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                <td className={styles.weekCell}>
                  <i className="ti ti-calendar" /> {w.weekLabel}
                </td>
                <td className={styles.centered}>{w.branches}</td>
                <td className={styles.amount}>{php(w.expense)}</td>
                <td className={styles.amount}>{php(w.revenue)}</td>
                <td className={`${styles.amount} ${w.net >= 0 ? styles.positive : styles.negative}`}>
                  {php(w.net)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Per-branch breakdown ── */}
      <div className={styles.sectionRow}>
        <div className={styles.sectionTitle}>Branch Breakdown</div>
        <div className={styles.filterWrap}>
          <label className={styles.filterLabel}>Filter by Week:</label>
          <select
            className={styles.filterSelect}
            value={weekFilter}
            onChange={e => setWeekFilter(e.target.value)}
          >
            <option value="All">All Weeks</option>
            {uniqueWeeks.map(w => (
              <option key={w.weekStart} value={w.weekStart}>{w.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Week</th>
              <th>Branch</th>
              <th>Weekly Revenue</th>
              <th>Supply Order Expense</th>
              <th>Net Profit</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className={styles.empty}>No records match the selected week.</td></tr>
            ) : filtered.map((r, i) => (
              <tr key={r.id} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                <td className={styles.weekCell}>
                  <i className="ti ti-calendar" />
                  {r.weekLabel || formatWeekLabel(r.weekStart)}
                </td>
                <td className={styles.branchName}>{r.branchName || '—'}</td>
                <td className={styles.amount}>{php(r.weeklyRevenue)}</td>
                <td className={styles.amount}>{php(r.supplyExpense)}</td>
                <td className={`${styles.amount} ${(r.netProfit || 0) >= 0 ? styles.positive : styles.negative}`}>
                  {php(r.netProfit)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
